import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { connect } from "react-redux";
import styled from "styled-components";
import axios from "axios";
import { setAlert } from "../../actions/alert";
import { handleScraperError } from "../../utils/errorHandler";

const CreateButtonContainer = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 1000;

  /* On mobile, position above the bottom sheet */
  @media only screen and (max-width: 599px) {
    bottom: 220px; /* Above collapsed bottom sheet (~200px) + some padding */
  }
`;

const PlusButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${props => props.$notAuthenticated ? 'var(--color-gray)' : 'var(--color-primary)'};
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  box-shadow: var(--shadow-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform var(--transition-base), background-color var(--transition-base);

  &:hover {
    background-color: ${props => props.$notAuthenticated ? 'var(--color-gray-dark, #666)' : 'var(--color-primary-light)'};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 400px;
  width: 100%;
  padding: 24px;
  box-shadow: var(--shadow-xl);
  position: relative;
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 20px;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    background-color: var(--color-gray-light);
    color: var(--color-text-primary);
  }
`;

const ModalIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--color-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 16px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  margin: 0 0 12px;
`;

const ModalDescription = styled.p`
  font-size: 15px;
  color: var(--color-text-secondary);
  text-align: center;
  margin: 0 0 20px;
  line-height: 1.5;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--color-gray-light);

  &:last-child {
    border-bottom: none;
  }

  i {
    color: var(--color-primary);
    font-size: 18px;
    margin-top: 2px;
  }

  span {
    font-size: 14px;
    color: var(--color-text-primary);
    line-height: 1.4;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ModalButton = styled(Link)`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  text-align: center;
  text-decoration: none;
  transition: background-color 0.2s, transform 0.1s;

  &:active {
    transform: scale(0.98);
  }
`;

const PrimaryButton = styled(ModalButton)`
  background-color: var(--color-primary);
  color: white;

  &:hover {
    background-color: var(--color-primary-dark);
  }
`;

const SecondaryButton = styled(ModalButton)`
  background-color: var(--color-gray-light);
  color: var(--color-text-primary);

  &:hover {
    background-color: var(--color-gray);
  }
`;

const IconContainer = styled.div`
  position: absolute;
  bottom: 70px;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-end;
  transition: opacity var(--transition-base), transform var(--transition-base);
  opacity: ${props => props.expanded ? 1 : 0};
  transform: ${props => props.expanded ? 'translateY(0)' : 'translateY(10px)'};
  pointer-events: ${props => props.expanded ? 'auto' : 'none'};
`;

const IconButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: var(--color-primary);
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  box-shadow: var(--shadow-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform var(--transition-base), background-color var(--transition-base);
  
  &:hover {
    background-color: var(--color-primary-light);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const InputContainer = styled.div`
  position: absolute;
  bottom: ${props => props.show ? '70px' : '60px'};
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity var(--transition-base), transform var(--transition-base);
  opacity: ${props => props.show ? 1 : 0};
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(10px)'};
  pointer-events: ${props => props.show ? 'auto' : 'none'};
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.i`
  position: absolute;
  left: 12px;
  color: #757575;
  font-size: 16px;
  z-index: 1;
`;

const TextInput = styled.input`
  width: 280px;
  padding: 12px 12px 12px 40px;
  border: 2px solid var(--color-primary);
  border-radius: 24px;
  font-size: 16px; /* 16px prevents iOS auto-zoom on focus */
  outline: none;
  background: white;
  box-shadow: var(--shadow-lg);
  transition: border-color var(--transition-base);
  
  &:focus {
    border-color: var(--color-primary-light);
  }
  
  &::placeholder {
    color: #9e9e9e;
  }
`;

const SubmitButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--color-primary);
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  box-shadow: var(--shadow-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform var(--transition-base), background-color var(--transition-base);
  
  &:hover {
    background-color: var(--color-primary-light);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const CreateButton = ({ token, setAlert }) => {
  const [expanded, setExpanded] = useState(false);
  const [inputMode, setInputMode] = useState(null); // 'link' or 'pencil' or null
  const [linkUrl, setLinkUrl] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const linkInputRef = useRef(null);
  const pencilInputRef = useRef(null);
  const containerRef = useRef(null);

  // Auto-expand when navigating with ?create=true query param
  useEffect(function autoExpandFromCreateParam() {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === 'true' && token) {
      setExpanded(true);
      // Clear the query param from URL without adding to history
      params.delete('create');
      const newSearch = params.toString();
      navigate(
        { pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' },
        { replace: true }
      );
    }
  }, [location.search, token, navigate, location.pathname]);

  // Close when clicking outside
  useEffect(function setupClickOutsideListener() {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setExpanded(false);
        setInputMode(null);
        setLinkUrl("");
        setRestaurantName("");
      }
    };

    if (expanded || inputMode) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [expanded, inputMode]);

  // Auto-focus input when it appears
  useEffect(function autoFocusInputOnModeChange() {
    if (inputMode === 'link' && linkInputRef.current) {
      linkInputRef.current.focus();
    } else if (inputMode === 'pencil' && pencilInputRef.current) {
      pencilInputRef.current.focus();
    }
  }, [inputMode]);

  // State for showing auth tooltip
  const [showAuthTooltip, setShowAuthTooltip] = useState(false);

  // Handle Escape key
  useEffect(function setupEscapeKeyListener() {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && inputMode) {
        setInputMode(null);
        setLinkUrl("");
        setRestaurantName("");
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [inputMode]);

  const handlePlusClick = () => {
    // If not authenticated, show tooltip instead
    if (!token) {
      setShowAuthTooltip(!showAuthTooltip);
      return;
    }
    if (inputMode) {
      setInputMode(null);
      setLinkUrl("");
      setRestaurantName("");
    }
    setExpanded(!expanded);
  };

  const handleLinkClick = () => {
    setExpanded(false);
    setInputMode('link');
  };

  const handlePencilClick = () => {
    setExpanded(false);
    setInputMode('pencil');
  };

  const handleLinkSubmit = async (e) => {
    e.preventDefault();
    if (!linkUrl.trim()) return;

    setLoading(true);
    try {
      const url = linkUrl.trim().replace(/(^\w+:|^)\/\//, "");
      const response = await axios.post("/api/restaurant_builder/process_url_async", {
        url: url,
      });

      // Navigate to processing page immediately
      navigate(`/create/processing/${response.data.jobId}`);
    } catch (error) {
      setLoading(false);
      handleScraperError(error, setAlert);
    }
    setLinkUrl("");
    setInputMode(null);
  };

  const handlePencilSubmit = (e) => {
    e.preventDefault();
    if (!restaurantName.trim()) return;

    // Navigate to RestaurantNameAddressLoc with just the name
    navigate("/create/v2/address", { state: { name: restaurantName.trim() } });
    setRestaurantName("");
    setInputMode(null);
  };

  const handleDocumentClick = () => {
    setExpanded(false);
    setInputMode(null);
    if (pdfInputRef.current) {
      pdfInputRef.current.click();
    }
  };

  const handleCameraClick = () => {
    setExpanded(false);
    setInputMode(null);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setAlert("File size exceeds 10MB limit. Please select a smaller file.", "failure");
      e.target.value = "";
      return;
    }

    setLoading(true);
    try {
      const postData = new FormData();
      postData.append("image", file);

      const response = await axios.post(
        "/api/restaurant_builder/upload_async",
        postData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Clear file input before navigating
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (e.target) {
        e.target.value = "";
      }

      // Navigate to processing page immediately
      navigate(`/create/processing/${response.data.jobId}`);
    } catch (error) {
      setLoading(false);
      handleScraperError(error, setAlert);
      // Clear file input on error too
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const handlePdfChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      setAlert("File size exceeds 20MB limit. Please select a smaller file.", "failure");
      e.target.value = "";
      return;
    }

    setLoading(true);
    try {
      const postData = new FormData();
      postData.append("pdf", file);

      const response = await axios.post(
        "/api/restaurant_builder/upload_pdf_async",
        postData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Clear file input before navigating
      if (pdfInputRef.current) {
        pdfInputRef.current.value = "";
      }
      if (e.target) {
        e.target.value = "";
      }

      // Navigate to processing page immediately
      navigate(`/create/processing/${response.data.jobId}`);
    } catch (error) {
      setLoading(false);
      handleScraperError(error, setAlert);
      // Clear file input on error too
      if (pdfInputRef.current) {
        pdfInputRef.current.value = "";
      }
      if (e.target) {
        e.target.value = "";
      }
    }
  };

  return (
    <CreateButtonContainer ref={containerRef}>
      <IconContainer expanded={expanded && !inputMode}>
        <IconButton
          onClick={handleLinkClick}
          title="Create from website link"
          aria-label="Create restaurant from website link"
          disabled={loading}
        >
          <i className="fas fa-link"></i>
        </IconButton>
        <IconButton
          onClick={handlePencilClick}
          title="Type restaurant name"
          aria-label="Create restaurant by typing name"
          disabled={loading}
        >
          <i className="fas fa-pencil-alt"></i>
        </IconButton>
        <IconButton
          onClick={handleDocumentClick}
          title="Upload PDF menu"
          aria-label="Upload PDF menu"
          disabled={loading}
        >
          <i className="fas fa-file-pdf"></i>
        </IconButton>
        <IconButton
          onClick={handleCameraClick}
          title="Upload menu image"
          aria-label="Upload menu image"
          disabled={loading}
        >
          <i className="fas fa-camera"></i>
        </IconButton>
      </IconContainer>
      
      {/* Link URL Input */}
      <InputContainer show={inputMode === 'link'}>
        <form onSubmit={handleLinkSubmit}>
          <InputWrapper>
            <InputIcon className="fas fa-link" />
            <TextInput
              ref={linkInputRef}
              type="text"
              placeholder="restaurant url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              disabled={loading}
            />
          </InputWrapper>
          <SubmitButton type="submit" disabled={loading || !linkUrl.trim()}>
            <i className="fas fa-arrow-right"></i>
          </SubmitButton>
        </form>
      </InputContainer>

      {/* Pencil Name Input */}
      <InputContainer show={inputMode === 'pencil'}>
        <form onSubmit={handlePencilSubmit}>
          <InputWrapper>
            <InputIcon className="fas fa-pencil-alt" />
            <TextInput
              ref={pencilInputRef}
              type="text"
              placeholder="restaurant name"
              value={restaurantName}
              onChange={(e) => setRestaurantName(e.target.value)}
              disabled={loading}
            />
          </InputWrapper>
          <SubmitButton type="submit" disabled={loading || !restaurantName.trim()}>
            <i className="fas fa-arrow-right"></i>
          </SubmitButton>
        </form>
      </InputContainer>

      {/* Auth modal for unauthenticated users - rendered via portal to escape stacking context */}
      {showAuthTooltip && !token && createPortal(
        <ModalOverlay onClick={() => setShowAuthTooltip(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalCloseButton onClick={() => setShowAuthTooltip(false)}>
              <i className="fas fa-times" />
            </ModalCloseButton>
            <ModalIcon>
              <i className="fas fa-plus" />
            </ModalIcon>
            <ModalTitle>Add Restaurants to VegifyIt</ModalTitle>
            <ModalDescription>
              Join our community and help others find vegan options!
            </ModalDescription>
            <FeatureList>
              <FeatureItem>
                <i className="fas fa-search" />
                <span><strong>Check any menu</strong> — paste a restaurant link and our AI finds vegan options instantly</span>
              </FeatureItem>
              <FeatureItem>
                <i className="fas fa-camera" />
                <span><strong>Snap a photo</strong> — upload a menu image and we'll analyze it for you</span>
              </FeatureItem>
              <FeatureItem>
                <i className="fas fa-map-marker-alt" />
                <span><strong>Add to the map</strong> — help others discover vegan-friendly spots in your area</span>
              </FeatureItem>
            </FeatureList>
            <ModalButtons>
              <SecondaryButton to="/login" onClick={() => setShowAuthTooltip(false)}>
                Log In
              </SecondaryButton>
              <PrimaryButton to="/signup" onClick={() => setShowAuthTooltip(false)}>
                Sign Up Free
              </PrimaryButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>,
        document.body
      )}

      <PlusButton
        onClick={handlePlusClick}
        aria-label={!token ? "Sign up to add restaurants" : expanded ? "Close creation options" : "Open creation options"}
        aria-expanded={token ? expanded : undefined}
        disabled={loading}
        $notAuthenticated={!token}
        $loading={loading}
      >
        {loading ? (
          <i className="fas fa-spinner fa-spin"></i>
        ) : (
          <i className={`fas fa-${expanded || inputMode ? "times" : "plus"}`}></i>
        )}
      </PlusButton>
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageChange}
      />
      <HiddenFileInput
        ref={pdfInputRef}
        type="file"
        accept="application/pdf"
        onChange={handlePdfChange}
      />
    </CreateButtonContainer>
  );
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
});

export default connect(mapStateToProps, { setAlert })(CreateButton);
